import { Box, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import useCustomDispatch from 'redux/dispatch';
import {
  getLanguages,
  getLogos,
  getOrganizationProfile,
  getTemplates
} from 'redux/slices/organization';
import { CustomDataGrid } from '../../../components/datagrid/Custom';
import { RootState, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import CountryFlag from '../../../components/icons/CountryFlag';
import Icon from 'components/icons/Icon';
import { DataGridKey } from 'redux/slices/datagrid';
import { EditListItem } from 'components/datagrid/listItems/EditListItem';
import { useTranslator } from 'translation/useTranslator';
import { Template } from '../../../@types/campaign';
import { fDate } from '@nps.today/component-library';

let thumbHeight = 64;

const ThumbImgStyle = styled('img')(({ theme }) => ({
  maxWidth: 70,
  maxHeight: thumbHeight,
  margin: theme.spacing(0, 2)
}));

interface TemplateListProps {
  onSelectionChange: (ids: number[]) => void;
}

export const TemplateList: FunctionComponent<TemplateListProps> = ({ onSelectionChange }) => {
  const translator = useTranslator();
  const DATA_GRID_KEY = DataGridKey.TemplateList;
  const { density } = useSelector((state: RootState) => state.datagrid);
  const { templates, isLoading } = useSelector((state: RootState) => state.organization);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set()
  });

  const navigate = useNavigate();
  const customDispatch = useCustomDispatch();

  useEffect(() => {
    customDispatch({ action: getTemplates, disableSuccessMessage: true });
    customDispatch({ action: getLogos, disableSuccessMessage: true });
    customDispatch({ action: getOrganizationProfile, disableSuccessMessage: true });
    customDispatch({ action: getLanguages, disableSuccessMessage: true });
  }, [customDispatch]);

  useEffect(() => {
    if (density[DATA_GRID_KEY] === 'compact') {
      thumbHeight = 32;
    } else if (density[DATA_GRID_KEY] === 'standard') {
      thumbHeight = 64;
    } else if (density[DATA_GRID_KEY] === 'comfortable') {
      thumbHeight = 128;
    }
  }, [density, DATA_GRID_KEY]);

  const columns: GridColDef<Template>[] = [
    {
      field: 'cover',
      headerName: translator.logo(),
      width: 120,
      align: 'center',
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        return <ThumbImgStyle src={params.row.logoURL} />;
      }
    },
    {
      field: 'id'
    },
    {
      field: 'name',
      headerName: translator.templateName(),
      flex: 1,
      hideable: false
    },
    {
      field: 'language',
      headerName: translator.language(),
      flex: 1,
      hideable: false,
      valueGetter: (_value, row) => row.language?.name ?? '',
      renderCell: (params) => {
        const languageName = params.row.language?.name ?? '';
        return (
          <>
            <CountryFlag country={languageName} />
            <div style={{ paddingLeft: '.5rem' }}>{languageName}</div>
          </>
        );
      }
    },
    {
      field: 'notificationTriggers',
      headerName: translator.npsNotifications(),
      flex: 1,
      hideable: false,
      valueGetter: (_value, row) => row.notificationTriggers?.length ?? 0
    },
    {
      field: 'created',
      headerName: translator.created(),
      flex: 1,
      hideable: false,
      renderCell: (params) => {
        return params.row.created ? fDate(params.row.created) : '';
      }
    },
    {
      field: 'type',
      headerName: translator.type(),
      width: 120,
      hideable: false,
      align: 'center',
      valueGetter: (_value, row) => {
        const manualDelivery = row.manualDelivery as boolean;
        const smsCampaign = row.smsCampaign as boolean;
        if (manualDelivery) {
          return translator.blockSendOuts();
        }
        if (smsCampaign) {
          return translator.smsCampaign();
        }
        return translator.emailCampaign();
      },
      renderCell: (params) => {
        const manualDelivery = params.row.manualDelivery as boolean;
        const smsCampaign = params.row.smsCampaign as boolean;
        return (
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
            {manualDelivery && (
              <Tooltip title={translator.blockSendOuts()}>
                <Icon type="manual" />
              </Tooltip>
            )}
            {smsCampaign && (
              <Tooltip title={translator.smsCampaign()}>
                <Icon type="sms" />
              </Tooltip>
            )}
            {!smsCampaign && (
              <Tooltip title={translator.emailCampaign()}>
                <Icon type="email" />
              </Tooltip>
            )}
          </Stack>
        );
      }
    },
    {
      field: 'edit',
      headerName: '',
      width: 60,
      editable: false,
      disableColumnMenu: true,
      hideable: false,
      align: 'center',
      hideSortIcons: true,
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <EditListItem
            onClick={() => {
              navigate(`${PATH_DASHBOARD.templates.edit.replace(':id', cellValues.id as string)}`);
            }}
          />
        );
      }
    }
  ];

  return (
    <Box>
      <CustomDataGrid
        dataGridKey={DATA_GRID_KEY}
        autoHeight
        loading={isLoading}
        checkboxSelection
        disableRowSelectionOnClick
        rows={templates ? templates.filter((temp) => temp.isTemplate === true) : []}
        columns={columns}
        pagination
        paginationModel={{ page: 0, pageSize: 25 }}
        pageSizeOptions={[25]}
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
          onSelectionChange(Array.from(newSelectionModel.ids ?? []).map(Number));
        }}
        rowSelectionModel={selectionModel}
      />
    </Box>
  );
};
